import { Component, EventEmitter, Input, Output } from '@angular/core';
import { EmailPreviewHydrationData } from '@galaxy/email-ui/email-builder';
import { ProjectionFilter } from '@vendasta/account-group';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import {
  BusinessContactSelectorModalComponent,
  ContactData,
  DialogData,
} from '@galaxy/email-ui/email-builder/src/components/business-contact-selector-modal/business-contact-selector-modal.component';
import {
  SendPreviewEmailDialogComponent,
  SendPreviewEmailDialogData,
} from '../../marketing/campaign/template-editing/send-preview-email-dialog/send-preview-email-dialog.component';
import { EmailTemplateSaver } from '../../marketing/email-template-saver';
import { ActivatedRoute } from '@angular/router';
import { SenderInfo } from '../../marketing/base-template-editing/template-editor-page/template-editor-page.component';

export type Layout = 'vertical' | 'horizontal';

export interface SelectedBusiness {
  accountGroupId: string;
  name: string;
  hasSalesperson: boolean;
  hasSnapshot: boolean;
}

@Component({
  selector: 'app-preview-business-contact-selector',
  templateUrl: './preview-business-contact-selector.component.html',
  styleUrl: './preview-business-contact-selector.component.scss',
  standalone: false,
})
export class PreviewBusinessContactSelectorComponent {
  protected business: SelectedBusiness;
  protected contactData: ContactData;
  protected showWarning: boolean;
  protected warningKey: string;
  private dialogData: DialogData;
  private readonly locale: string;
  protected _hydrationData: EmailPreviewHydrationData;
  protected _senderInfo: SenderInfo;
  protected readonly layout: Layout = 'horizontal';

  @Input() set hydrationData(data: EmailPreviewHydrationData) {
    this._hydrationData = data;
    this.setBusinessData(data);
    this.setDialogData(data);
  }

  @Input() set senderInfo(senderInfo: SenderInfo) {
    this._senderInfo = senderInfo;
  }

  @Output() businessSelected = new EventEmitter<SelectedBusiness>();
  @Output() contactSelected = new EventEmitter<string>();

  private setDialogData(data: EmailPreviewHydrationData) {
    this.dialogData = {
      partnerId: data?.partnerID,
      marketId: data?.marketID,
      projectionFilterOverride: {
        napData: true,
        accountGroupExternalIdentifiers: true,
        snapshotReports: true,
      } as ProjectionFilter,
    };
  }

  private setBusinessData(data: EmailPreviewHydrationData) {
    this.business = {
      accountGroupId: data?.business?.accountGroupId,
      name: data?.business?.name || '',
      hasSalesperson: data?.business?.hasSalesperson,
      hasSnapshot: data?.business?.hasSnapshot,
    };
    this.showWarning = this.shouldShowWarning(this.business);
    this.warningKey = this.getWarningKey(this.business);
  }

  private shouldShowWarning(business: SelectedBusiness): boolean {
    return !(business?.hasSalesperson && business?.hasSnapshot);
  }

  private getWarningKey(business: SelectedBusiness): string {
    if (!business.hasSalesperson && !business.hasSnapshot) {
      return 'EMAIL_TEMPLATES.PREVIEW.BUSINESS_SELECTOR.NO_SNAPSHOT_OR_SALESPERSON_WARNING';
    }
    if (!business.hasSnapshot) {
      return 'EMAIL_TEMPLATES.PREVIEW.BUSINESS_SELECTOR.NO_SNAPSHOT_WARNING';
    }
    if (!business.hasSalesperson) {
      return 'EMAIL_TEMPLATES.PREVIEW.BUSINESS_SELECTOR.NO_SALESPERSON_WARNING';
    }
    return '';
  }

  constructor(
    private readonly dialog: MatDialog,
    private readonly saver: EmailTemplateSaver,
    private readonly route: ActivatedRoute,
  ) {
    this.locale = this.route.snapshot.queryParams.locale;
  }

  async openDialog() {
    const dialog = this.dialog.open(BusinessContactSelectorModalComponent, {
      data: this.dialogData,
      width: '500px',
      minHeight: '496px',
    });

    const result = await firstValueFrom(dialog.afterClosed());
    if (result?.business) {
      this.business = {
        accountGroupId: result.business.accountGroupId,
        name: result.business.napData.companyName,
        hasSnapshot: result.business.snapshotReports?.snapshots?.length > 0,
        hasSalesperson: !!result.business.accountGroupExternalIdentifiers?.salesPersonId,
      };
      this.showWarning = this.shouldShowWarning(this.business);
      this.contactData = null;
      this.businessSelected.emit(this.business);
      this.contactSelected.emit('');
    }

    if (result?.contact) {
      this.contactData = {
        contactID: result.contact.contactID,
        accountGroupID: result.contact.accountGroupID,
        name: result.contact.name,
        email: result.contact.email,
      };
      this.business = null;
      this.contactSelected.emit(result.contact.contactID);
      this.businessSelected.emit(this.business);
    }
  }

  async openPreviewEmailDialog() {
    const contentData = this.saver.getContentData();
    const htmlContent = this.saver.getHTML();
    this.dialog.open(SendPreviewEmailDialogComponent, {
      data: {
        partnerID: this.dialogData.partnerId,
        marketID: this.dialogData.marketId,
        business: this.business,
        subjectLine: contentData.subjectLine,
        emailHtmlContent: htmlContent,
        locale: this.locale,
        contact: this.contactData,
        senderInfo: this._senderInfo,
      } as SendPreviewEmailDialogData,
      width: '463px',
    });
  }
}
